import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
	STORAGE_KEYS,
	getLocale,
	getQuestionText,
	getOptionText,
	getDefaultAnswers,
	calculateResult,
	buildSummary,
	buildSummaryDescription,
	getStoredSession,
	saveSession,
	saveResultLink,
	openPopupById,
	questionBank,
	localeTexts,
} from './crmQuizHelpers.js';
import {
	ensureQuestionnaireSession,
	dispatchQuestionnaireContextUpdate,
} from './crmQuizQuestionnaireFlow.js';

const normalizeQuizResult = (rawResult = {}) => {
	const recommendationKey = rawResult.recommendationKey || rawResult.recommendation_key || 'smart';
	const smartPct = Number(rawResult.smartPct ?? rawResult.smart_pct ?? 0);
	const d365Pct = Number(rawResult.d365Pct ?? rawResult.d365_pct ?? 0);
	const smartScore = Number(rawResult.smartScore ?? rawResult.smart_score ?? smartPct);
	const d365Score = Number(rawResult.d365Score ?? rawResult.d365_score ?? d365Pct);

	return {
		...rawResult,
		recommendationKey,
		recommendation_key: recommendationKey,
		recommendationTitle: rawResult.recommendationTitle || rawResult.recommendation || (recommendationKey === 'd365' ? 'Microsoft Dynamics 365' : 'SMART CRM'),
		smartPct,
		d365Pct,
		smartScore,
		d365Score,
		isSmartWinner: recommendationKey !== 'd365',
		isTie: recommendationKey === 'tie',
	};
};

const getFinalParamPayload = () => {
	const params = new URLSearchParams(window.location.search);
	const finalParam = params.get('final');

	if (!finalParam) {
		return null;
	}

	const match = String(finalParam).match(/^(smart|d365|tie)-(\d{1,3})%?$/i);
	if (!match) {
		return null;
	}

	const recommendationKey = match[1].toLowerCase();
	const winnerPct = Math.max(0, Math.min(100, Number(match[2])));
	const smartPct = recommendationKey === 'd365' ? 100 - winnerPct : winnerPct;
	const d365Pct = recommendationKey === 'd365' ? winnerPct : 100 - winnerPct;

	return {
		success: true,
		variant: recommendationKey,
		resultUrl: window.location.href,
		result_url: window.location.href,
		result: normalizeQuizResult({
			recommendation_key: recommendationKey,
			recommendation: recommendationKey === 'd365' ? 'Microsoft Dynamics 365' : 'SMART CRM',
			smart_pct: smartPct,
			d365_pct: d365Pct,
			smart_score: smartPct,
			d365_score: d365Pct,
		}),
	};
};

const buildFinalUrl = (resultData) => {
	const normalized = normalizeQuizResult(resultData);
	const recommendationKey = normalized.recommendationKey === 'd365' ? 'd365' : 'smart';
	const winnerPct = recommendationKey === 'd365' ? normalized.d365Pct : normalized.smartPct;
	const url = new URL(window.location.href);

	url.searchParams.set('final', `${recommendationKey}-${winnerPct}%`);
	url.hash = '';

	return url.href;
};

const getCrmResultLabel = (result) => {
	if (!result) {
		return '';
	}

	return result.recommendationTitle || result.recommendation || '';
};

const renderRecommendationBody = (body, title) => {
	const productTitle = title || '';
	const titleIndex = productTitle ? body.indexOf(productTitle) : -1;

	if (titleIndex === -1) {
		return body;
	}

	return (
		<>
			{body.slice(0, titleIndex)}
			<strong>{productTitle}</strong>
			{body.slice(titleIndex + productTitle.length)}
		</>
	);
};

function QuizApp() {
	const locale = getLocale();
	const texts = localeTexts[locale] || localeTexts.en;
	const storedSession = getStoredSession();
	const [currentIndex, setCurrentIndex] = useState(() => {
		if (storedSession && Number.isInteger(storedSession.currentIndex)) {
			return Math.max(0, Math.min(storedSession.currentIndex, questionBank.length - 1));
		}

		return 0;
	});
	const [answers, setAnswers] = useState(() => {
		if (storedSession && Array.isArray(storedSession.answers) && storedSession.answers.length) {
			return storedSession.answers;
		}

		return getDefaultAnswers();
	});
	const [submittedResult, setSubmittedResult] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState('');

	const sessionPayload = useMemo(() => ({ currentIndex, answers }), [currentIndex, answers]);
	const progress = submittedResult ? 100 : Math.round(((currentIndex + 1) / questionBank.length) * 100);
	const debugLog = () => {};

	useEffect(() => {
		const finalPayload = getFinalParamPayload();
		if (finalPayload) {
			debugLog('final param payload detected', {
				payloadKeys: Object.keys(finalPayload || {}),
				resultKeys: Object.keys(finalPayload?.result || {}),
			});
			window.CRMQuizLeadContext = finalPayload;
			setSubmittedResult(finalPayload);
		}
	}, []);

	useEffect(() => {
		if (!submittedResult) {
			saveSession(sessionPayload);
		}
	}, [sessionPayload, submittedResult]);

	const result = useMemo(() => calculateResult(answers), [answers]);
	const displayResult = normalizeQuizResult(submittedResult?.result || result);
	const summary = useMemo(() => buildSummary(answers, locale), [answers, locale]);
	const activeLeadContext = window.CRMQuizLeadContext || {};

	const selectOption = (optionIndex) => {
		const question = questionBank[currentIndex];

		setAnswers((prevAnswers) => {
			const nextAnswers = [...prevAnswers];

			if (question.multiple) {
				const current = Array.isArray(nextAnswers[currentIndex]) ? [...nextAnswers[currentIndex]] : [];
				const existingIndex = current.indexOf(optionIndex);
				if (existingIndex === -1) {
					current.push(optionIndex);
				} else {
					current.splice(existingIndex, 1);
				}
				nextAnswers[currentIndex] = current;
				return nextAnswers;
			}

			nextAnswers[currentIndex] = optionIndex;
			return nextAnswers;
		});
	};

	const hasAnswer = () => {
		const question = questionBank[currentIndex];
		if (question.multiple) {
			return Array.isArray(answers[currentIndex]) && answers[currentIndex].length > 0;
		}

		return answers[currentIndex] !== null && answers[currentIndex] !== undefined;
	};

	const persistAndNavigate = (payload) => {
		const finalUrl = buildFinalUrl(payload?.result || result);
		debugLog('persistAndNavigate', {
			finalUrl,
			payloadKeys: Object.keys(payload || {}),
			resultKeys: Object.keys(payload?.result || {}),
		});
		const nextPayload = {
			...payload,
			success: true,
			result: normalizeQuizResult(payload?.result || result),
			resultUrl: finalUrl,
			result_url: finalUrl,
			crm_result: getCrmResultLabel(normalizeQuizResult(payload?.result || result)),
			questionSummary: summary,
			survey_answers: buildSummaryDescription(summary),
			variant: payload?.result?.recommendation_key || payload?.result?.recommendationKey || result.recommendationKey || 'smart',
		};

		saveResultLink(nextPayload);
		ensureQuestionnaireSession(nextPayload);
		dispatchQuestionnaireContextUpdate();
		setSubmittedResult(nextPayload);
		window.history.replaceState({ crmQuizResult: true }, '', finalUrl);
	};

	const submitResult = async (leadOverride = null) => {
		setIsSubmitting(true);
		setError('');
		debugLog('submitResult start', {
			leadKeys: Object.keys(leadOverride || activeLeadContext?.lead || {}),
			answerCount: answers.length,
		});

		try {
			const apiUrl = `${String(window.CRMQuizSettings.restUrl || '').replace(/\/$/, '')}/result`;
			const response = await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.CRMQuizSettings.nonce,
				},
				body: JSON.stringify({
					locale,
					answers,
					lead: leadOverride || activeLeadContext?.lead || {},
					result_id: activeLeadContext?.resultId || '',
					result_slug: activeLeadContext?.resultSlug || '',
					result_url: buildFinalUrl(result),
						result: {
							recommendation: result.recommendationTitle,
							recommendation_key: result.recommendationKey,
							confidence: result.confidence,
							smart_score: result.smartScore,
							d365_score: result.d365Score,
							smart_pct: result.smartPct,
							d365_pct: result.d365Pct,
							title: `${texts.resultsTitle} - ${result.recommendationTitle}`,
						},
						crm_result: getCrmResultLabel(result),
						questionSummary: summary,
						survey_answers: buildSummaryDescription(summary),
						description: buildSummaryDescription(summary),
					}),
				});

			if (!response.ok) {
				debugLog('submitResult failed response', {
					status: response.status,
					statusText: response.statusText,
				});
				throw new Error(texts.error);
			}

			const payload = await response.json();
			debugLog('submitResult success', {
				payloadKeys: Object.keys(payload || {}),
			});
			persistAndNavigate({
				...payload,
				answers,
				result: normalizeQuizResult(result),
				lead: leadOverride || activeLeadContext?.lead || {},
				resultSlug: payload.result_slug || activeLeadContext?.resultSlug || '',
			});
		} catch (submitError) {
			debugLog('submitResult error', {
				message: submitError instanceof Error ? submitError.message : String(submitError),
			});
			setError(submitError.message || texts.error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const openConsultPopup = () => {
		debugLog('openConsultPopup', {
			submittedResultKeys: Object.keys(submittedResult || {}),
			activeLeadKeys: Object.keys(activeLeadContext || {}),
		});
		const payload = {
			locale,
			answers,
				result: displayResult,
				crm_result: getCrmResultLabel(displayResult),
				questionSummary: summary,
				survey_answers: buildSummaryDescription(summary),
				resultId: submittedResult?.result_id || activeLeadContext?.resultId || '',
				resultSlug: submittedResult?.result_slug || activeLeadContext?.resultSlug || '',
				resultUrl: submittedResult?.result_url || window.localStorage.getItem(STORAGE_KEYS.resultUrl) || '',
			lead: activeLeadContext?.lead || {},
			description: buildSummaryDescription(summary),
		};

		window.CRMQuizLeadContext = payload;
		window.localStorage.setItem(STORAGE_KEYS.resultData, JSON.stringify(payload));
		window.localStorage.setItem(STORAGE_KEYS.resultUrl, payload.resultUrl || '');
		ensureQuestionnaireSession(payload);
		dispatchQuestionnaireContextUpdate();
		openPopupById('#popup-demo', false);
	};

	useEffect(() => {
		if (!submittedResult) {
			return undefined;
		}

		debugLog('submitted result effect', {
			submittedResultKeys: Object.keys(submittedResult || {}),
			summaryCount: summary.length,
		});
		ensureQuestionnaireSession({
			...(window.CRMQuizLeadContext || {}),
			...(submittedResult || {}),
			questionSummary: summary,
		});
		dispatchQuestionnaireContextUpdate();
	}, [submittedResult, summary]);

	const restartQuiz = () => {
		const url = new URL(window.location.href);
		url.searchParams.delete('final');
		window.history.replaceState({}, '', url.href);
		window.sessionStorage.removeItem(STORAGE_KEYS.session);
		window.localStorage.removeItem(STORAGE_KEYS.session);
		window.localStorage.removeItem(STORAGE_KEYS.draft);
		setCurrentIndex(0);
		setAnswers(getDefaultAnswers());
		setSubmittedResult(null);
		setError('');
	};

	const renderOptionIndicator = (question) => (question.multiple ? <span className="crm-quiz-option-check" /> : <span className="crm-quiz-option-dot" />);

	const currentQuestion = questionBank[currentIndex];
	const recommendation = texts.recommendations[displayResult.recommendationKey] || texts.recommendations.smart;
	const recommendationBadge = texts.recommendedBadge || texts.recommendation;
	const storedResultUrl = window.localStorage.getItem(STORAGE_KEYS.resultUrl) || submittedResult?.resultUrl || '';
	const savedResultUrl = storedResultUrl.includes('final=') ? storedResultUrl : '';
	const savedResultVariant =
		submittedResult?.variant ||
		submittedResult?.result?.recommendation_key ||
		activeLeadContext?.variant ||
		activeLeadContext?.result?.recommendation_key ||
		displayResult.recommendationKey ||
		'smart';

	return (
		<div className={`crm-quiz-page${submittedResult ? ' crm-quiz-page--results' : ''}`}>
			<div className="crm-quiz-glow crm-quiz-glow--left" />
			<div className="crm-quiz-glow crm-quiz-glow--right" />
			<div className="crm-quiz-shell">
				<header className="crm-quiz-header">
					<div className="crm-quiz-badge">
						<span>●</span>
						{texts.badge}
					</div>
					<h1>{texts.title}</h1>
					<p>{texts.description}</p>
					{savedResultUrl ? (
						<a className="crm-quiz-saved-link" href={savedResultUrl} data-success="1" data-variant={savedResultVariant}>
							{texts.continueLink}
						</a>
					) : null}
				</header>

				{!submittedResult ? (
					<div className="crm-quiz-screen">
						<div className="crm-quiz-card">
							<div className="crm-quiz-progress">
								<div className="crm-quiz-progress-meta">
									<span>
										{texts.progress} <strong>{currentIndex + 1}</strong> {texts.of} {questionBank.length}
									</span>
									<span>{progress}%</span>
								</div>
								<div className="crm-quiz-progress-bar">
									<div className="crm-quiz-progress-fill" style={{ width: `${progress}%` }} />
								</div>
							</div>

							<div className="crm-quiz-question-number">{`${texts.questionLabel} ${String(currentIndex + 1).padStart(2, '0')}`}</div>
							<div className="crm-quiz-question-text">{getQuestionText(currentQuestion, locale)}</div>
							{currentQuestion.multiple ? <div className="crm-quiz-multi-hint">{texts.multiHint}</div> : null}
							<div className="crm-quiz-options">
								{currentQuestion.options.map((option, optionIndex) => {
									const isSelected = currentQuestion.multiple
										? Array.isArray(answers[currentIndex]) && answers[currentIndex].includes(optionIndex)
										: answers[currentIndex] === optionIndex;

									return (
										<button
											key={`${currentQuestion.id}-${optionIndex}`}
											type="button"
											className={`crm-quiz-option${isSelected ? ' is-selected' : ''}`}
											onClick={() => selectOption(optionIndex)}
										>
											{renderOptionIndicator(currentQuestion)}
											<span className="crm-quiz-option-label">{getOptionText(option, locale)}</span>
										</button>
									);
								})}
							</div>

							<div className="crm-quiz-actions">
								{currentIndex > 0 ? (
									<button type="button" className="crm-quiz-btn crm-quiz-btn--secondary" onClick={() => setCurrentIndex((value) => Math.max(value - 1, 0))}>
										{texts.back}
									</button>
								) : null}
								<button
									type="button"
									className="crm-quiz-btn crm-quiz-btn--primary"
									onClick={() => {
										if (!hasAnswer()) {
											return;
										}
										if (currentIndex < questionBank.length - 1) {
											setCurrentIndex((value) => value + 1);
											return;
										}
										submitResult();
									}}
									disabled={!hasAnswer() || isSubmitting}
								>
									{isSubmitting ? texts.loading : currentIndex === questionBank.length - 1 ? texts.results : texts.next}
								</button>
							</div>
						</div>
					</div>
				) : (
					<div className="crm-quiz-results">
						<div className="crm-quiz-results-card crm-quiz-card">
							<div className="crm-quiz-header crm-quiz-header--results">
								<h1>{texts.resultsTitle}</h1>
								</div>

								<div className="crm-quiz-score-wrap">
									<div className={`crm-quiz-score-item${displayResult.isSmartWinner && !displayResult.isTie ? ' is-winner' : ''}`}>
										{displayResult.isSmartWinner && !displayResult.isTie ? <div className="crm-quiz-winner-badge">{recommendationBadge}</div> : null}
										<div className={`crm-quiz-score-card${displayResult.isSmartWinner && !displayResult.isTie ? ' is-winner' : ''}`}>
											<div className="crm-quiz-score-label">{texts.scoreLabel}</div>
											<div className="crm-quiz-score-name">SMART CRM</div>
											<div className="crm-quiz-score-number">{displayResult.smartPct}%</div>
											<div className="crm-quiz-score-pts">{texts.scoreRelevance}</div>
											<div className="crm-quiz-bar-track">
												<div
													className={`crm-quiz-bar-fill${displayResult.isSmartWinner && !displayResult.isTie ? ' crm-quiz-bar-fill--winner' : ' crm-quiz-bar-fill--muted'}`}
													style={{ width: `${displayResult.smartPct}%` }}
												/>
											</div>
										</div>
									</div>
									<div className={`crm-quiz-score-item${!displayResult.isSmartWinner && !displayResult.isTie ? ' is-winner is-winner-d365' : ''}`}>
										{!displayResult.isSmartWinner && !displayResult.isTie ? <div className="crm-quiz-winner-badge">{recommendationBadge}</div> : null}
										<div className={`crm-quiz-score-card${!displayResult.isSmartWinner && !displayResult.isTie ? ' is-winner is-winner-d365' : ''}`}>
											<div className="crm-quiz-score-label">{texts.scoreLabel}</div>
											<div className="crm-quiz-score-name">Dynamics 365</div>
											<div className="crm-quiz-score-number">{displayResult.d365Pct}%</div>
											<div className="crm-quiz-score-pts">{texts.scoreRelevance}</div>
											<div className="crm-quiz-bar-track">
												<div
													className={`crm-quiz-bar-fill${!displayResult.isSmartWinner && !displayResult.isTie ? ' crm-quiz-bar-fill--winner' : ' crm-quiz-bar-fill--muted'}`}
													style={{ width: `${displayResult.d365Pct}%` }}
												/>
											</div>
										</div>
									</div>
								</div>

								<div className={`crm-quiz-recommendation crm-quiz-recommendation--${displayResult.recommendationKey}`}>
									<p className="crm-quiz-rec-desc">
										<span>{renderRecommendationBody(recommendation.body, recommendation.title)}</span>
									</p>
								{recommendation.features.length ? (
									<div className="crm-quiz-rec-features">
										{recommendation.features.map((feature) => (
											<div className="crm-quiz-rec-feature" key={feature}>
												<span className="crm-quiz-rec-dot" />
												<span>{feature}</span>
											</div>
										))}
									</div>
								) : null}
							</div>

							<div className="crm-quiz-lead-panel">
								<div className="crm-quiz-lead-copy">
									<div className="crm-quiz-lead-title">{texts.leadTitle}</div>
									<div className="crm-quiz-lead-descr">{texts.leadDescr}</div>
								</div>
								{activeLeadContext?.lead?.email ? (
									<div className="crm-quiz-lead-state">{activeLeadContext.lead.email}</div>
								) : (
									<div className="crm-quiz-lead-state">{texts.noLead}</div>
								)}
							</div>

							{error ? <div className="crm-quiz-error">{error}</div> : null}

							<div className="crm-quiz-results-actions crm-quiz-results-actions--center">
								<button type="button" className="crm-quiz-btn crm-quiz-btn--secondary" onClick={restartQuiz}>
									{texts.restartNew}
								</button>
								<a
									href="#popup-demo"
									className="crm-quiz-btn crm-quiz-btn--primary crm-quiz-btn--result-cta"
									data-success="1"
									data-variant={displayResult.recommendationKey}
									data-result-url={submittedResult?.result_url || submittedResult?.resultUrl || ''}
									onClick={(event) => {
										event.preventDefault();
										openConsultPopup();
									}}
								>
									{texts.consultCta}
								</a>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export const mountCrmQuiz = () => {
	const rootElement = document.getElementById('crm-quiz-root');
	if (!rootElement) {
		return;
	}

	const root = createRoot(rootElement);
	root.render(<QuizApp />);
};
